import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import { TSetPaginationFn, TUseTranslationTfn } from "../../data/AppType";
import { GET_FIRM_CREDIT_BALANCE, GET_FIRM_CREDIT_LEDGER } from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { handleError, sweetAlertError } from "../../utils/AppFunctions";
import { CREDIT_TYPE_LABEL_KEY } from "../../utils/PurchaseConstants";
import {
  CREDIT_LEDGER_REASON_ENUM,
  CREDIT_TYPE_ENUM,
} from "../../utils/PurchaseEnumerations";
import { formatPurchaseDateTime } from "../../utils/PurchaseFormat";

export interface ICreditBalanceCell {
  credit_type: CREDIT_TYPE_ENUM;
  label: string;
  balance: number | null;
  is_unlimited: boolean;
  is_provided: boolean;
  max_purchasable: number;
}

export interface IFirmCreditBalance {
  firm_id: string;
  firm_name: string;
  balances: ICreditBalanceCell[];
}

export interface ICreditLedgerRow {
  id: string;
  credit_type: CREDIT_TYPE_ENUM;
  reason: CREDIT_LEDGER_REASON_ENUM;
  balance_before: number | null;
  balance_after: number | null;
  delta: number | null;
  note: string | null;
  created_date: string;
  created_by_name: string | null;
  reference: string | null;
  order_id: string | null;
}

interface ICreditLedgerCells {
  reason: (row: ICreditLedgerRow) => JSX.Element;
  change: (row: ICreditLedgerRow) => JSX.Element;
}

export const getCreditLedgerFields = (
  cells: ICreditLedgerCells,
  t: TUseTranslationTfn,
): ITableFields<ICreditLedgerRow>[] => [
  {
    name: t("date"),
    key: "created_date",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditLedgerRow>) =>
      formatPurchaseDateTime(data.created_date),
  },
  {
    name: t("credit_type"),
    key: "credit_type",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditLedgerRow>) => {
      const row: ICreditLedgerRow = data;
      return t(CREDIT_TYPE_LABEL_KEY[row.credit_type]);
    },
  },
  {
    name: t("reason"),
    key: "reason",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditLedgerRow>) => cells.reason(data),
  },
  {
    name: t("credits"),
    key: "delta",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-1",
    format: ({ data }: ITabelFieldViewProps<ICreditLedgerRow>) => cells.change(data),
  },
  {
    name: t("details"),
    key: "note",
    sort: false,
    type: TABLE_FIELD_TYPE.text,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-3",
  },
  {
    name: t("reference"),
    key: "reference",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditLedgerRow>) => data.reference ?? "-",
  },
  {
    name: t("created_by"),
    key: "created_by_name",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditLedgerRow>) => data.created_by_name ?? "-",
  },
];

export const fetchFirmCreditBalance = async (
  firmId: string,
): Promise<IFirmCreditBalance | undefined> => {
  try {
    const response = await GET_FIRM_CREDIT_BALANCE(firmId);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      return response.data;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "fetchFirmCreditBalance");
  }
  return undefined;
};

export const fetchFirmCreditLedger = async (
  firmId: string,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
): Promise<ICreditLedgerRow[]> => {
  try {
    const response = await GET_FIRM_CREDIT_LEDGER(firmId, pagination, null);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      setPagination(response.data.pagination);
      return response.data.item ?? [];
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "fetchFirmCreditLedger");
  }
  return [];
};
